import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Trash, Plus, Settings, FileText, Download, ExternalLink, Edit } from 'lucide-react';
import { GhostButton } from '../ui/GhostButton';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { SoftwareResourceModal } from '../modals/SoftwareResourceModal';
import { ResourceTypeManagementModal } from '../modals/ResourceTypeManagementModal';
import { useToast } from '../../context/ToastContext';
import * as Icons from 'lucide-react';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
export const SoftwareTab = () => {
    const { toast } = useToast();
    const {
        softwareResources,
        deleteSoftwareResource,
        updateSoftwareResource
    } = usePIM();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [editingResource, setEditingResource] = useState(null);

    // Sorting
    const { items: sortedResources, requestSort, sortConfig } = useSortableData(softwareResources, { key: 'type_sort_order', direction: 'ascending' });

    const getIcon = (iconName) => {
        const Icon = Icons[iconName] || Icons.File;
        return <Icon size={16} />;
    };

    const handleEdit = (resource) => {
        setEditingResource(resource);
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setEditingResource(null);
    };

    const handleVisibilityChange = async (res, newVisibility) => {
        try {
            await updateSoftwareResource(res.id, {
                ...res,
                release_date: res.release_date ? res.release_date.split('T')[0] : null,
                visibility: newVisibility
            });
            toast.success("Visibility updated");
        } catch (err) {
            toast.error("Failed to update visibility");
            console.error("Failed to update visibility", err);
        }
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">Software & Resources</h2>
                    <p className="text-xs text-stone-500 mt-1">Manage software, drivers, and documentation.</p>
                </div>
                <div className="flex gap-3">
                    <GhostButton onClick={() => setIsAddModalOpen(true)}><Plus size={16} /> Add Resource</GhostButton>
                    <GhostButton onClick={() => setIsManageModalOpen(true)}><Settings size={16} /> Manage Types</GhostButton>
                </div>
            </div>

            <div className="bg-stone-900/50 border border-stone-700 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <th className="bg-stone-950 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-stone-800 w-10"></th>
                                <SortableHeader label="Name" sortKey="name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Type" sortKey="type_sort_order" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Version" sortKey="version" currentSort={sortConfig} onSort={requestSort} />
                                 <SortableHeader label="Date" sortKey="release_date" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Visibility" sortKey="visibility" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Path / Link" sortKey="file_path" currentSort={sortConfig} onSort={requestSort} />
                                <th className="bg-stone-950 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-stone-800 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedResources.length === 0 ? (
                                <tr>
                                     <td colSpan="8" className="px-4 py-8 text-center text-stone-500 text-sm">
                                        No resources found for this charge.
                                    </td>
                                </tr>
                            ) : (
                                sortedResources.map(res => (
                                    <tr key={res.id} className="hover:bg-stone-800/30 transition-colors border-b border-stone-800 last:border-0">
                                        <td className="px-4 py-0.5 text-stone-400">
                                            {getIcon(res.type_icon)}
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-200 font-medium">{res.name}</td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400">
                                            <Badge color={res.type_color || 'blue'} size="sm">{res.type}</Badge>
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400">{res.version}</td>
                                         <td className="px-4 py-0.5 text-sm text-stone-400">
                                            {res.release_date ? new Date(res.release_date).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-4 py-0.5">
                                            <select
                                                value={res.visibility || 'sensitive'}
                                                onChange={(e) => handleVisibilityChange(res, e.target.value)}
                                                className={`text-[10px] font-bold border rounded px-1 py-0.5 bg-stone-950 outline-none cursor-pointer focus:border-blue-500 transition-colors ${
                                                    (res.visibility === 'public') ? 'text-green-400 border-green-800/50' : 
                                                    (res.visibility === 'blocked') ? 'text-red-400 border-red-800/50' :
                                                    'text-amber-500 border-amber-800/50'
                                                }`}
                                            >
                                                <option value="public" className="text-green-400 bg-stone-900">PUBLIC</option>
                                                <option value="sensitive" className="text-amber-500 bg-stone-900">SENSITIVE</option>
                                                <option value="blocked" className="text-red-400 bg-stone-900">BLOCKED</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400 truncate max-w-[100px]" title={res.file_path}>
                                            {res.mime_type === 'link' ? (
                                                <a href={res.file_path} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:underline truncate">
                                                    <ExternalLink size={12} className="flex-shrink-0" /> <span className="truncate">{res.file_path}</span>
                                                </a>
                                            ) : (
                                                <span className="flex items-center gap-1 truncate">
                                                    <FileText size={12} className="flex-shrink-0" /> <span className="truncate">{res.file_path}</span>
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-0.5 text-right">
                                            <div className="flex justify-end gap-2">
                                                {res.mime_type !== 'link' && (
                                                    <a
                                                        href={res.file_path}
                                                        download={res.original_filename}
                                                        className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-blue-400 hover:border-blue-900/50 hover:bg-blue-900/10 flex items-center justify-center transition"
                                                        title="Download"
                                                    >
                                                        <Download size={14} />
                                                    </a>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(res)}
                                                    className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-yellow-400 hover:border-yellow-900/50 hover:bg-yellow-900/10 flex items-center justify-center transition"
                                                    title="Edit"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button
                                                    onClick={() => deleteSoftwareResource(res.id)}
                                                    className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"
                                                    title="Delete"
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <SoftwareResourceModal
                isOpen={isAddModalOpen}
                onClose={handleCloseAddModal}
                resourceToEdit={editingResource}
            />

            <ResourceTypeManagementModal
                isOpen={isManageModalOpen}
                onClose={() => setIsManageModalOpen(false)}
            />
        </div>
    );
};
